/**
 * The live `DashboardSource` — the same canvas the fixture drives, over the real api
 * (`specs/M10-D1-dashboards.md` §8.1).
 *
 * The one behaviour that lives here rather than in the canvas is the **refresh rule** (§6.2): a
 * widget's data is a query, not a row, so a `cell.changed` / `item.created` / `item.deleted`
 * event on a connected board does not patch anything — it invalidates the widget queries, on a
 * 5 s trailing debounce so a burst of cell writes costs one re-aggregation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { widgetRegistry } from '@elizmonday/shared';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import type { CanvasParent, CanvasSettingsOptions, DashboardSource, WidgetRow } from './types';

/** §6.2: a burst of cell writes costs one re-aggregation, not one per write. */
export const WIDGET_REFRESH_DEBOUNCE_MS = 5_000;

const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export function useDashboardSource(
  parent: CanvasParent,
  options: { readOnly?: boolean; boardIds?: readonly string[] } = {},
): DashboardSource {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const timezone = useMemo(browserTimezone, []);
  /*
   * The staleness stamp §6.2's refresh carries. It is part of the `dashboards.data` **input**,
   * so a refresh re-keys the query rather than only invalidating it: the server caches an
   * aggregation for 15 s, which would otherwise answer the 5 s-later refetch with exactly the
   * numbers the refresh existed to replace (P-182). Every client that saw the same change event
   * sends the same stamp, so the recomputation is still one per change, not one per viewer.
   */
  const [revision, setRevision] = useState('');
  const boardIds = useMemo(() => [...(options.boardIds ?? [])], [options.boardIds?.join(',')]);

  const widgetsQuery = useQuery({ ...trpc.dashboards.widgets.queryOptions(parent), staleTime: 10_000 });
  // The pickers every settings pane and the canvas filter draw from; one round trip per canvas.
  const optionsQuery = useQuery({ ...trpc.dashboards.settingsOptions.queryOptions(parent), staleTime: 60_000 });
  const dashboardQuery = useQuery({
    ...trpc.dashboards.get.queryOptions({ id: parent.id }),
    enabled: parent.kind === 'dashboard',
    staleTime: 10_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: trpc.dashboards.pathKey() });
  }, [queryClient, trpc]);

  const add = useMutation({
    mutationFn: (registryId: string) =>
      client.dashboards.addWidget.mutate({
        parent,
        registryId,
        name: widgetRegistry.find(registryId)?.title ?? 'Widget',
        settings: {},
      }),
    onSuccess: invalidate,
  });
  const move = useMutation({
    mutationFn: (layouts: Array<{ id: string; x: number; y: number; w: number; h: number }>) =>
      client.dashboards.moveWidgets.mutate({ parent, layouts }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => client.dashboards.removeWidget.mutate({ id }),
    onSuccess: invalidate,
  });
  const rename = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      client.dashboards.updateWidget.mutate({ id: input.id, name: input.name }),
    onSuccess: invalidate,
  });
  const setFilter = useMutation({
    mutationFn: (next: unknown) => client.dashboards.update.mutate({ id: parent.id, filter: next }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; settings: Record<string, unknown> }) =>
      client.dashboards.updateWidget.mutate({ id: input.id, settings: input.settings }),
    onSuccess: invalidate,
  });

  // The debounced refresh. `useWidgetData` is a hook, so the timer lives at the source level.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingStamp = useRef<string>('');
  const refresh = useCallback(
    (stamp?: string) => {
      if (stamp !== undefined && stamp !== '') pendingStamp.current = stamp;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        setRevision(pendingStamp.current === '' ? String(Date.now()) : pendingStamp.current);
        void queryClient.invalidateQueries({ queryKey: trpc.dashboards.data.pathKey() });
      }, WIDGET_REFRESH_DEBOUNCE_MS);
    },
    [queryClient, trpc],
  );
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return useMemo<DashboardSource>(
    () => ({
      parent,
      boardIds,
      useWidgets: () => ({
        data: widgetsQuery.data as WidgetRow[] | undefined,
        isPending: widgetsQuery.isPending,
      }),
      // eslint-disable-next-line react-hooks/rules-of-hooks -- a source method IS the hook call site
      useSettingsOptions: () => ({
        data: optionsQuery.data as CanvasSettingsOptions | undefined,
        isPending: optionsQuery.isPending,
      }),
      useWidgetData: (widgetId: string) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const query = useQuery({
          ...trpc.dashboards.data.queryOptions({ parent, widgetId, timezone, revision }),
          staleTime: 15_000,
        });
        return { data: query.data, isPending: query.isPending };
      },
      mutations:
        options.readOnly === true
          ? null
          : {
              add: (registryId) => add.mutate(registryId),
              move: (layouts) => move.mutate(layouts),
              remove: (widgetId) => remove.mutate(widgetId),
              rename: (widgetId, name) => rename.mutate({ id: widgetId, name }),
              update: (widgetId, settings) => update.mutate({ id: widgetId, settings }),
            },
      ...(parent.kind === 'dashboard' && options.readOnly !== true
        ? {
            filter: {
              value: (dashboardQuery.data as { filter?: unknown } | undefined)?.filter ?? null,
              set: (next: unknown) => setFilter.mutate(next),
            },
          }
        : {}),
      reload: invalidate,
      refresh,
    }),
    [
      parent,
      boardIds,
      widgetsQuery.data,
      widgetsQuery.isPending,
      trpc,
      timezone,
      revision,
      options.readOnly,
      add,
      move,
      remove,
      rename,
      update,
      setFilter,
      optionsQuery.data,
      optionsQuery.isPending,
      dashboardQuery.data,
      invalidate,
      refresh,
    ],
  ) as DashboardSource;
}
